import {
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { Button } from 'src/components/Button/Button.component';
import Loader from 'src/components/Loader/Loader.component';
import NeedHelpBase from 'src/components/NeedHelpButton/NeedHelpBase.component';
import NeedHelpInline from 'src/components/NeedHelpButton/NeedHelpInline.component';
import Translate from 'src/components/Translate/Translate.component';
import { PaymentFlowContextType } from 'src/pages/paymentFlow/PaymentFlowPage.types';
import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { RoutePath } from 'src/routers/routers.config';
import { confirmSetupAttemptMethodApi } from 'src/services/payments/payments.request';
import { useCurrentTransactionStore, useProfileStore } from 'src/store/store';
import { refactorFormatDate } from 'src/utils/functions/refactorFormatDate';
import { StripePaymentElementChangeEvent } from '@stripe/stripe-js';

type StateStripe = {
  idTransaction?: string | number;
  amount?: string | number;
  showPaymentSetupPage?: boolean;
  isBackup?: boolean;
};

const CheckoutForm = () => {
  const stripe = useStripe();
  const elements = useElements();
  const {
    changeMethodHandler,
    payNowHandler,
    confirmOrderHandler,
    setBackupHandler,
    type,
    formattedAmount,
    paymentMethods,
    paymentMethodBackup,
  } = useOutletContext<PaymentFlowContextType>();
  const currentOrder = useProfileStore((state) => state.currentOrder);
  const setTransaction = useCurrentTransactionStore(
    (state) => state.setTransaction,
  );
  const purchasing = currentOrder?.OrderId !== undefined;
  const location = useLocation();
  const locState: StateStripe = location.state?.stateStripe
    ? location.state?.stateStripe
    : null;
  const isBackup = locState.isBackup ?? false;
  const currentTransaction = useCurrentTransactionStore((state) => state);

  const nextInstallmentDueDate = useCurrentTransactionStore(
    (state) => state.nextInstallmentDueDate,
  );
  const [paymentMethod, setPaymentMethod] = useState('');
  const changedMethod = useCurrentTransactionStore(
    (state) => state.ChangedMethod,
  );

  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [showNeedHelp, setShowNeedHelp] = useState(false);
  const [isPending, setIsLoading] = useState(false);

  const { mutate: mutateSetup, isPending: isLoadingSetup } = useMutation({
    mutationFn: confirmSetupAttemptMethodApi,
    onSuccess: (res, variables) => {
      const { IsValid, PaymentMethod } = res.data.ResultSet;
      if (!IsValid) {
        const d = new Date();
        d.setTime(d.getTime() + 24 * 3600 * 1000);
        localStorage.setItem('opyn-invalid-payment-method', 'true');
        navigate(-1);
        return;
      } else {
        localStorage.setItem('opyn-invalid-payment-method', 'false');
      }
      if (type === 'change') {
        changeMethodHandler({
          paymentMethodId: PaymentMethod.Id,
        });
        return;
      }
      if (
        !variables.isBackup &&
        PaymentMethod.Type === 'sepa_debit' &&
        type === 'pay'
      ) {
        setTransaction('ChangedMethod', PaymentMethod);
        navigate(`${RoutePath.paymentFlow}${RoutePath.setMethods}`);
        return;
      }
      if (variables.isBackup) {
        setBackupHandler({ BackupPaymentMethodId: PaymentMethod.Id });
        return;
      }

      purchasing
        ? confirmOrderHandler({
            paymentMethodId: PaymentMethod.Id,
            orderGuid: currentOrder?.OrderGuid,
          })
        : payNowHandler({
            paymentMethodId: PaymentMethod.Id,
          });
    },
    onError: () => navigate(RoutePath.oops),
  });

  useEffect(() => {
    if (!stripe) return;
    const clientSecret = new URLSearchParams(window.location.search).get(
      'payment_intent_client_secret',
    );
    if (!clientSecret) return;

    stripe.retrieveSetupIntent(clientSecret).then(({ setupIntent }) => {
      switch (setupIntent?.status) {
        case 'succeeded':
          setMessage('Payment succeeded!');
          break;
        case 'processing':
          setMessage('Your payment is processing.');
          break;
        case 'requires_payment_method':
          setMessage('Your payment was not successful, please try again.');
          break;
        default:
          setMessage('Something went wrong.');
          break;
      }
    });
  }, [stripe]);

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }
    setIsLoading(true);
    await stripe
      .confirmSetup({
        elements,
        redirect: 'if_required',
      })
      .then((res: any) => {
        setIsLoading(false);
        if (res.error) {
          const { errMessage } = res.error ?? {};
          (window as any).parent.postMessage(
            JSON.stringify({
              status: 'reject',
              message: 'The payment has failed',
            }),
            '*',
          );
          if (
            errMessage === 'setup_intent_authentication_failure' ||
            errMessage === 'payment_intent_authentication_failure'
          )
            setMessage(errMessage);
          else setShowNeedHelp(true);
        } else {
          mutateSetup({
            stripePaymentMethodId: res.setupIntent.payment_method,
            stripeSetupId: res.setupIntent.id,
            isBackup,
          });
        }
      })
      .catch(() => {
        setIsLoading(false);
        setMessage('stripe.confirmSetup() error');
      });
    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment, then
    // redirected to the `return_url`.
  };

  const handleChangePaymentMethod = (e: StripePaymentElementChangeEvent) => {
    setPaymentMethod(e.value.type);
  };

  const getTranslationId = (
    paymentMethod: string,
    paymentIntent: boolean | undefined,
    type: string,
  ) => {
    if (type === 'pay') {
      if (paymentMethod === 'card') {
        return changedMethod?.Type === 'sepa_debit' ||
          (!paymentMethodBackup &&
            paymentMethods.find((e) => e.Type === 'sepa_debit'))
          ? 'lbl.confirm'
          : currentTransaction.paymentIntent
            ? 'text.payNow'
            : 'lbl.confirmPayFirstInstallmentDate';
      } else if (paymentMethod === 'sepa_debit') {
        return 'lbl.confirm';
      }
    } else if (type === 'change') {
      if (paymentMethod === 'card') {
        return currentTransaction.paymentIntent
          ? 'text.confirmAndPay'
          : 'lbl.confirmPayNextInstallmentDate';
      } else if (paymentMethod === 'sepa_debit') {
        return currentTransaction.paymentIntent
          ? 'text.confirmAndPay'
          : 'lbl.confirmPayNextInstallmentDate';
      }
    }
    return 'lbl.confirm';
  };

  const safeFormatDate = (date: string | undefined, format: string): string => {
    return date ? refactorFormatDate(date, format) : '';
  };

  return (
    <>
      {(isPending || isLoadingSetup) && (
        <Loader overlayViewMode="fullscreen" active viewMode="fluid" />
      )}
      <form id="payment-form" onSubmit={handleSubmit}>
        <PaymentElement
          id="payment-element"
          onChange={handleChangePaymentMethod}
        />
        <br />
        <Button
          type={'submit'}
          minWidth="100%"
          variant="Primary"
          disabled={!stripe || !elements}
          id="submit"
        >
          <Translate
            id={getTranslationId(
              paymentMethod,
              currentTransaction.paymentIntent,
              type,
            )}
            data={{
              paymentValue: formattedAmount,
              firstInstallmentDueDate: safeFormatDate(
                currentOrder.FirstInstallmentDueDate,
                'DD/MM/YYYY',
              ),
              nextInstallmentDueDate: safeFormatDate(
                nextInstallmentDueDate,
                'DD/MM/YYYY',
              ),
            }}
          />
        </Button>
      </form>
      {message && (
        <div className="w-full pt-2 text-danger" id="payment-message">
          {message}
        </div>
      )}
      {showNeedHelp && (
        <div className="w-full pt-2 text-danger" id="payment-message">
          <Translate id="error.stripeFail" />
          <NeedHelpBase BaseComponent={NeedHelpInline} />
        </div>
      )}
    </>
  );
};

export default CheckoutForm;
